import React from 'react';
import { Link } from 'react-router-dom';
import baseline, { appTextStyle } from '../../app-ui/baseline';
import { albumPath } from '../../components/content/albumPath';
import SponsoredExposure from '../ads/SponsoredExposure';
import PlainImage from '../images/PlainImage';
import useSearchBridgeFocus from './useSearchBridgeFocus';
import { CampaignLink, SponsoredLabel } from '../ads/SponsoredContent';
import './SearchHomeChrome.css';

// Submitted SearchScreen appends campaigns after up to two organic bridges.
export function bridgeSponsoredItems(items, campaigns, artistLabel) {
  const eligible = campaigns.filter(
    (c) => c.albums?.length && c.placements?.[0]?.format === 'NATIVE_ALBUM',
  );
  if (!eligible.length) return items;
  const ids = eligible.flatMap((c) => c.albums.map((a) => String(a.id)));
  const organic = items.filter((item) => !ids.some((id) => item.id.includes(id)));
  return [
    ...organic.slice(0, 2),
    ...eligible.map((campaign) => {
      const album = campaign.albums[0];
      return {
        id: `sponsored-continue-${campaign.id}`,
        title: album.title,
        meta: album.artist?.name || artistLabel,
        image: album.imageUrl,
        campaign,
        to: album.slug
          ? albumPath(album)
          : albumPath({ albumSource: album.albumSource, sourceId: album.sourceId }),
      };
    }),
  ];
}
function BridgeIcon({ chevron = false }) {
  return (
    <svg
      width={chevron ? 20 : 22}
      height={chevron ? 20 : 22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={chevron ? 1.8 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {chevron ? (
        <path d="m9 18 6-6-6-6" />
      ) : (
        <>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </>
      )}
    </svg>
  );
}
export default function SearchBridgeRows({ items }) {
  const focus = useSearchBridgeFocus();
  const s = baseline.search.chrome;
  const variables = {
    '--bridge-min': `${s.eraBridge.minHeight}px`,
    '--bridge-cover': `${s.eraBridgeLeadImage.width}px`,
    '--bridge-gap': `${s.eraBridge.gap}px`,
    '--bridge-copy-gap': `${s.eraBridgeCopy.gap}px`,
    '--bridge-padding': `${s.eraBridge.paddingVertical}px`,
    '--bridge-offset': `${s.eraBridgeList.marginTop}px`,
    '--bridge-ad-gap': `${s.eraBridgeSponsoredLabel.marginTop}px`,
  };
  return (
    <div className="pp-search-bridge-list" style={variables} {...focus}>
      {items.map((item) => {
        const body = (
          <>
            {item.image?.trim() ? (
              <span className="pp-search-bridge-cover">
                <PlainImage
                  src={item.image}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'inherit',
                  }}
                />
              </span>
            ) : (
              <span className="pp-search-bridge-cover pp-search-bridge-placeholder">
                <BridgeIcon />
              </span>
            )}
            <div
              className={`pp-search-bridge-copy${item.campaign ? ' pp-search-bridge-sponsored-copy' : ''}`}
            >
              <span
                className="pp-search-bridge-title"
                style={appTextStyle(baseline.tokens.typography.bodyMedium)}
              >
                {item.title}
              </span>
              <span
                className="pp-search-bridge-meta"
                style={appTextStyle(baseline.contentText.metadata)}
              >
                {item.meta}
              </span>
              {item.campaign && (
                <SponsoredLabel campaign={item.campaign} placementKey="SEARCH_CONTINUE" />
              )}
            </div>
            <span className="pp-search-bridge-chevron">
              <BridgeIcon chevron />
            </span>
          </>
        );
        const label = `${item.title} ${item.meta}`;
        return item.campaign ? (
          <SponsoredExposure
            key={item.id}
            campaignId={item.campaign.id}
            placementKey="SEARCH_CONTINUE"
            className="pp-search-bridge-row pp-search-bridge-sponsored"
          >
            <CampaignLink
              campaign={item.campaign}
              placementKey="SEARCH_CONTINUE"
              destination={item.to}
              className="pp-search-bridge-ad-link"
            >
              <span className="pp-visually-hidden">{label}</span>
            </CampaignLink>
            {body}
          </SponsoredExposure>
        ) : (
          <Link key={item.id} to={item.to} className="pp-search-bridge-row" aria-label={label}>
            {body}
          </Link>
        );
      })}
    </div>
  );
}
